import { Component, OnInit, PLATFORM_ID, Inject, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { faChartBar, faListAlt } from '@fortawesome/free-regular-svg-icons';
import { faFlask, faBox, faChartLine, faSearch, faSignOutAlt, faAngleLeft, faAngleRight, faGlobe, faCog, faGraduationCap, faDatabase } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { TutorialService } from '../../services/tutorial.service';
import { TenantService } from '../../services/tenant.service';
import { TenantConfig } from '../../interfaces/tenant.interface';
import { ThemeService } from '../../services/theme.service';
import { Subscription } from 'rxjs';

interface SidebarItem {
  route: string;
  labelKey: string;
  icon: IconDefinition;
}

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  faChartBar = faChartBar;
  faFlask = faFlask;
  faBox = faBox;
  faChartLine = faChartLine;
  faListAlt = faListAlt;
  faSearch = faSearch;
  faSignOutAlt = faSignOutAlt;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faGlobe = faGlobe;
  faCog = faCog;
  faGraduationCap = faGraduationCap;
  faDatabase = faDatabase;

  sidebarItems: SidebarItem[] = [];

  @Input() currentLanguage: string = 'pt';
  @Input() isMobileOpen: boolean = false; 
  
  @Output() languageChanged = new EventEmitter<string>();
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() sidebarCollapsed = new EventEmitter<boolean>();
  
  isCollapsed: boolean = false;
  userEmail: string = '';

  logoSmall = 'assets/logo/delfos_logo.png';
  logoFull = 'assets/logo/delfos_logo_and_text.png';
  brandFooterLogo = 'assets/img/di2win_logo_dark.png';

  private tenantSub?: Subscription;
  private themeSub?: Subscription;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private tutorialService: TutorialService,
    private tenantService: TenantService,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.checkIfShouldCollapse();
    this.buildSidebarItems();
    if (isPlatformBrowser(this.platformId)) {
      this.userEmail = sessionStorage.getItem('email') || '';
    }

    this.tenantSub = this.tenantService.tenant$.subscribe(tenant => {
      if (tenant) {
        this.logoSmall = tenant.logoSmall || 'assets/logo/delfos_logo.png';
        this.logoFull = tenant.logoFull || 'assets/logo/delfos_logo_and_text.png';
        this.buildSidebarItems();
      }
    });

    this.setBrandFooterLogo(this.themeService.currentTheme);
    this.themeSub = this.themeService.theme$.subscribe(theme => this.setBrandFooterLogo(theme));
  }

  ngOnDestroy(): void {
    this.tenantSub?.unsubscribe();
    this.themeSub?.unsubscribe();
  }

  private setBrandFooterLogo(theme: string): void {
    this.brandFooterLogo = theme === 'dark' ? 'assets/img/di2win_logo_white.png' : 'assets/img/di2win_logo_dark.png';
  }

  private buildSidebarItems(): void {
    const prefix = this.tenantService.getRoutePrefix();
    this.sidebarItems = [
      { route: `${prefix}/simulations`, labelKey: 'SIDEBAR.INTERACTIONS', icon: this.faChartLine },
      { route: `${prefix}/models`, labelKey: 'SIDEBAR.MODELS', icon: this.faFlask },
      { route: `${prefix}/archives`, labelKey: 'SIDEBAR.ARCHIVES', icon: this.faBox },
      { route: `${prefix}/references`, labelKey: 'SIDEBAR.REFERENCES', icon: this.faListAlt },
      { route: `${prefix}/settings/dashboard`, labelKey: 'SIDEBAR.DASHBOARD', icon: this.faChartBar },
    ];
  }

  get routePrefix(): string {
    return this.tenantService.getRoutePrefix();
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarCollapsed.emit(this.isCollapsed);
    this.collapsedChange.emit(this.isCollapsed);
  }

  private checkIfShouldCollapse(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (window.innerWidth < 768) {
        this.isCollapsed = true;
        this.collapsedChange.emit(this.isCollapsed);
        this.sidebarCollapsed.emit(this.isCollapsed);
      }
    }
  }

  openTutorial(): void {
    this.tutorialService.startManual();
  }

  logout(): void {
    sessionStorage.clear();
    const loginRoute = this.tenantService.buildRoute('/login');
    this.router.navigate(loginRoute);
  }
}